import { useEffect, useMemo } from 'react'
import { Alert, Col, Form, Row } from 'antd'
import { Formik, useFormikContext } from 'formik'
import { useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import * as Yup from 'yup'
import dayjs from 'dayjs'
import { useIsMobile } from '../../../hooks'
import { formElementPropsGenerator } from '../../../lib/forms'
import { DatePicker, Input, SubmitButton } from '../../Forms'

import api from '../../../api'

import PatientSelect from '../../Forms/PatientSelect'
import DeviceModelSelect from '../../Forms/DeviceModelSelect'

const TRANSMISSIONS_DEFAULTS = {
  patientId: undefined,
  firstName: '',
  lastName: '',
  dateOfBirth: undefined,
  deviceModelId: '',
  deviceSerialNumber: '',
  transmissionDate: dayjs(),
}

const TRANSMISSIONS_LABELS = {
  patientId: 'Patient',
  firstName: 'First Name',
  lastName: 'Last Name',
  dateOfBirth: 'Date of Birth',
  deviceModelId: 'Model #',
  deviceSerialNumber: 'Serial Number',
  cptCode: 'CPT Code',
  icd10Code: 'ICD-10 Code',
  authorized: 'Authorized',
  practiceMrn: 'Practice MRN',
  transmissionDate: 'Transmission Date',
}

const propsFor = formElementPropsGenerator({
  formName: 'transmission-form',
  labels: TRANSMISSIONS_LABELS,
})

// Keyed on `md`: below 768 the app is in its mobile layout and this form goes
// vertical, rather than holding a label column beside every input.
const labelCol = {
  md: { span: 6, offset: 0 },
}

const wrapperCol = {
  md: { span: 15 },
}

const actionsWrapperCol = {
  md: { span: 4, offset: 10 },
}

const buildValidationSchema = () => {
  const shape = {
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    dateOfBirth: Yup.date().max(new Date()).required(),
    deviceModelId: Yup.string().required(),
    deviceSerialNumber: Yup.string().required(),
    transmissionDate: Yup.date().required(),
  }

  return Yup.object().shape(shape)
}

function CustomPatientSelector({ practiceId = undefined, paramsPatientId = undefined }) {
  const { values, setValues } = useFormikContext()

  const { patientId } = values
  const id = paramsPatientId || patientId

  useEffect(() => {
    if (id) {
      api.patients.get(id).then(response => {
        setValues({
          ...TRANSMISSIONS_DEFAULTS,
          patientId: response.id,
          firstName: response.firstName,
          lastName: response.lastName,
          dateOfBirth: dayjs(response.birthDate),
          practiceMrn: response.practiceMrn,
          deviceModelId: response.currentDevice?.modelName,
          deviceSerialNumber: response.currentDevice?.serialNumber,
        })
      })
    } else {
      setValues({ ...TRANSMISSIONS_DEFAULTS })
    }
  }, [id, setValues])
  if (paramsPatientId) {
    return null
  }

  return (
    <PatientSelect
      {...propsFor('patientId')}
      practiceId={practiceId}
      placeholder="Search by name, DOB or serial number"
      allowClear
      showInactive
    />
  )
}

export default function PatientForm({ practiceId = undefined, onSubmit }) {
  const isMobile = useIsMobile()
  const validationSchema = useMemo(() => buildValidationSchema(), [])
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const paramsPatientId = searchParams.get('patientId')

  return (
    <Formik
      initialValues={TRANSMISSIONS_DEFAULTS}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, isSubmitting, submitCount, values }) => (
        <Form
          layout={isMobile ? 'vertical' : 'horizontal'}
          onFinish={handleSubmit}
          labelCol={isMobile ? undefined : labelCol}
          wrapperCol={isMobile ? undefined : wrapperCol}
        >
          <Alert
            message="If no patient exists, completing the form below with patient information will automatically create a new one."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <CustomPatientSelector
            practiceId={practiceId}
            paramsPatientId={paramsPatientId}
          />

          <Row>
            <Col span={24}>
              <Input {...propsFor('firstName')} disabled={!!values.patientId} />
            </Col>
            <Col span={24}>
              <Input {...propsFor('lastName')} disabled={!!values.patientId} />
            </Col>
            <Col span={24}>
              <DatePicker
                {...propsFor('dateOfBirth')}
                disabled={!!values.patientId}
                showToday={false}
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Input
                {...propsFor('practiceMrn')}
                disabled={!!values.patientId}
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <DeviceModelSelect
                {...propsFor('deviceModelId')}
                labelCol={{ md: { span: 6 } }}
                labelAlign="right"
                wrapperCol={{ md: { span: 15 } }}
                disabled={!!values.patientId}
              />
            </Col>
            <Col span={24}>
              <Input
                {...propsFor('deviceSerialNumber')}
                disabled={!!values.patientId}
              />
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <DatePicker
                {...propsFor('transmissionDate')}
                defaultValue={dayjs()}
              />
            </Col>
          </Row>

          <SubmitButton
            isSubmitting={isSubmitting}
            title="Create Transmission"
            isValid
            submitCount={submitCount}
            wrapperCol={actionsWrapperCol}
          />
        </Form>
      )}
    </Formik>
  )
}

CustomPatientSelector.propTypes = {
  practiceId: PropTypes.string,
  paramsPatientId: PropTypes.string,
}

PatientForm.propTypes = {
  practiceId: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
}
